import React, { useContext } from 'react';
import { Table } from './Table';
import { useIsWorkspaceAvailable, WorkspaceActionCreator } from 'hooks/useWorkspaceState';
import { WorkspaceContext } from '/views/workspace/workspaceContext';
import { SRSValuesResult } from 'srs-middleware';

/**
 * ChartWrapper for the Table Widget.
 * This wrapper handles complexity associated with workspace context and data from srs
 * in order to pass clean data to the table widget.
 */
export function TableWrapper({ dimensions, data: allData, cardIndex, loading }) {
	/** The number of records to fetch for infinite scrolling */
	const additionalRecordsToFetch: number = 100;

	const { dispatch, state } = useContext(WorkspaceContext) ?? {};
	const isInWorkspace = useIsWorkspaceAvailable();

	// data represents that data that is returned from the SRS Values call
	const { columns = [], data } = allData ?? {};

	// tableData is the data that is rendered into the 
	const [tableData, setTableData] = React.useState<any[]>([]);

	// Track whether the data is new to prevent an infinite loop when setting tableData in the effect hook below
	const [dataIsNew, setDataIsNew] = React.useState<boolean>(true);
	React.useEffect(() => {
		setDataIsNew(true);
	}, [data]);

	//pull the parameters from state
	const parameters = state?.explorer?.layout?.cards?.[0]?.endpointQuery?.options?.parameters || {};

	//data should be appended to the dataset if new data was triggerd by a scroll event
	const [shouldAppendData, setShouldAppendData] = React.useState<boolean>(false);

	//when data changes, update table data
	React.useEffect(() => {
		if(dataIsNew){
			let newTableData: any[] = [];
			//If we have new data or columns have changed, set the table data using the data from the server
			if (!shouldAppendData) {
				//prevColumnValue.current = columns;
				newTableData = data?.map((dataObject) => ({ documentId: dataObject.documentId, ...dataObject.propertyValues }));
			} else {
				newTableData = tableData.slice(); //create a copy of the current data
				setShouldAppendData(false);
				const currentIds = tableData.map((dataObject) => dataObject.documentId);
				data.forEach((dataObject: SRSValuesResult<any>) => {
					if (!currentIds.includes(dataObject.documentId)) {
						newTableData.push({ documentId: dataObject.documentId, ...dataObject.propertyValues });
					}
				});
			}	
			setTableData(newTableData);
			setDataIsNew(false);

		}
	}, [data, shouldAppendData, setTableData, dataIsNew, tableData]);

	/** On the sort event, request new data from the server (sorting is done server side) */
	const handleSort = (columnName: string, isDescending: boolean) => {
		setTableData([]);
		dispatch &&
			dispatch(
				WorkspaceActionCreator.patchParameters(cardIndex, {
					descending: isDescending,
					sortProperty: columnName,
				})
			);
	};

	/** Get more data */
	const loadAdditionalRecords = () => {
		if(!loading){
			setShouldAppendData(true);
			dispatch &&
				dispatch(
					WorkspaceActionCreator.patchParameters(cardIndex, {
						startIndex: tableData.length,
						endIndex: tableData.length + additionalRecordsToFetch,
					})
				);
		}
	};

	return (
		<Table
			data={tableData}
			height={dimensions.height}
			width={dimensions.width}
			rowHeight={24}
			headerHeight={24}
			rowCount={tableData ? tableData.length : 0}
			columnNames={columns}
			onSort={handleSort}
			loadAdditionalRecords={loadAdditionalRecords}
			readOnly={!isInWorkspace}
			sortBy={parameters?.sortProperty}
			sortIsDescending={parameters?.descending}
		/>
	);
}
